/**
 * @file key-travel.ts
 * @description GCOME_AP 磁轴键盘触发行程（快速触发）功能实现。
 *
 * 数据区：触发行程参数区 CMD 0xa0 GetKeyTriggerTravel / 0xa1 SetKeyTriggerTravel，
 * 每板载 128 键 × 8 字节 = 1024 字节（协议细节参见 docs/果来微科技_磁轴键盘AP通讯协议.md）：
 *   字节0 低5bit 轴体类型 + 高3bit 校验(0x5)；字节1 低4bit 触发类型 + 高4bit SOCD 策略；
 *   字节2-3 触发行程（0-399，0.01mm）；字节4-5 按下参数、字节6-7 释放参数，
 *   均为 16 位打包：低 9 位（bit0-8）= RT 灵敏度（0-511，实际上限随精度 0.01/0.005/0.001 变化），
 *   高 7 位（bit9-15）= 死区（0-127，0.01mm）；RT 精度由字节3 bit1-4 决定。
 *
 * 遵循 KeyTravelFeature 规范。GCOME_AP 无"RT 档位"概念，每键独立配置，
 * 因此通用接口的 rt 参数被忽略；getGlobalRTConfig/setGlobalRTConfig 不支持。
 * getKeyTravel 返回 family: KeyTravelFamily.Gcome 的 GcomeKeyTravel；setKeyTravel 接受
 * Partial<GcomeKeyTravel> 补丁并读-改-写合并。
 */

import {
  GcomeCalibrationValue,
  KeyTravelFamily,
  KeyTravelList,
  KeyTravelWriteList,
  keyboardCalibration,
  protocolCailbrationType,
} from '../../../../types/keyTravel'
import { baseKeyMap } from '../../../../types/keyboardKeys'
import { KeyTravelFeature } from '../../../../features/key-travel/contract'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import { GCOME_AP_CMD, GCOME_AP_REPORT } from '../../protocols/cmd'
import { calculateGcomeApAddress } from '../../protocols/address'
import {
  TRAVEL_KEY_COUNT,
  TRAVEL_KEY_SIZE,
  encodeTravelInfo,
  parseTravelInfo,
  readTravelBlock,
  writeTravelBlock,
} from '../../utils/advanced-keys'
import { encodeShaftTypeByte, decodeShaftTypeByte } from '../../../../types/shaftType'
import { mutateFuncBlock } from '../../utils/lightingCapabilities'
import {
  GCOME_CALIBRATION_KEY_COUNT,
  GCOME_CALIBRATION_ZONE_SIZE,
  parseCalibrationValues,
} from '../../utils/key-debug'

/**
 * @file key-travel.ts
 * @author Rapoo Device SDK Team
 * @date 2026-08-07
 * @description GCOME_AP 键程（触发行程/快速触发）功能
 */
export class GcomeApKeyTravel extends KeyTravelFeature {
  private get gcomeDevice(): GcomeApKeyboardDevice {
    return this.device as unknown as GcomeApKeyboardDevice
  }

  /** GCOME_AP 无全局 RT 档位（每键独立配置），统一视为未启用。 */
  getGlobalRTConfig(): Promise<number> {
    return Promise.resolve(0)
  }

  /** GCOME_AP 无全局 RT 档位。 */
  setGlobalRTConfig(_index: number): Promise<void> {
    return this.unsupported('GcomeApKeyTravel.setGlobalRTConfig')
  }

  /**
   * 通用接口：获取键盘所有键位的触发行程参数（128 键 × 8 字节，family: KeyTravelFamily.Gcome）。
   * GCOME_AP 无 RT 档位概念，rt 参数被忽略。
   * 绑定类键（如 0x93/0x94 查表键）的行程与快速触发存储于其被绑定键的该参数区。
   */
  async getKeyTravel(_rt: number): Promise<KeyTravelList> {
    const block = await readTravelBlock(this.gcomeDevice)
    return parseTravelInfo(block)
  }

  /** 通用接口：设置指定键位的触发行程（读-改-写，Partial 补丁按字段合并保留）。 */
  async setKeyTravel(keyTravelList: KeyTravelWriteList, index?: number): Promise<void[]> {
    if (!Array.isArray(keyTravelList) || keyTravelList.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Invalid key travel list')
    }
    const first = keyTravelList[0]
    const targets = index !== undefined ? [{ ...first, index }] : [...keyTravelList]
    const block = await readTravelBlock(this.gcomeDevice)
    const items = parseTravelInfo(block)
    for (const info of targets) {
      if (info.family !== KeyTravelFamily.Gcome) {
        throw new DeviceError(
          DeviceErrorCode.INVALID_PARAM,
          'GCOME_AP setKeyTravel requires family: KeyTravelFamily.Gcome',
        )
      }
      if (info.index < 0 || info.index >= TRAVEL_KEY_COUNT) continue
      items[info.index] = {
        family: KeyTravelFamily.Gcome,
        index: info.index,
        keyTravel: { ...items[info.index].keyTravel, ...info.keyTravel },
      }
    }
    await writeTravelBlock(this.gcomeDevice, encodeTravelInfo(items))
    return []
  }

  // ==================== 轴体类型（字节0 低5bit） ====================

  /** 读取所有键位的轴体类型 ID（从触发行程参数区字节0 解码）。 */
  async getKeyShaftTypes(): Promise<Array<{ index: number; shaftType: number }>> {
    const block = await readTravelBlock(this.gcomeDevice)
    const items: Array<{ index: number; shaftType: number }> = []
    for (let i = 0; i < TRAVEL_KEY_COUNT; i++) {
      items.push({ index: i, shaftType: decodeShaftTypeByte(block[i * TRAVEL_KEY_SIZE]) })
    }
    return items
  }

  /** 批量设置键位轴体类型（读-改-写）。 */
  async setKeyShaftTypes(entries: Array<{ index: number; shaftType: number }>): Promise<void> {
    if (!Array.isArray(entries) || entries.length === 0) return
    const block = await readTravelBlock(this.gcomeDevice)
    for (const { index, shaftType } of entries) {
      if (index < 0 || index >= TRAVEL_KEY_COUNT) continue
      block[index * TRAVEL_KEY_SIZE] = encodeShaftTypeByte(shaftType)
    }
    await writeTravelBlock(this.gcomeDevice, block)
  }

  /** 读取指定键位的轴体类型 ID。 */
  async getKeyShaftType(keyIndex: number): Promise<number> {
    if (!Number.isInteger(keyIndex) || keyIndex < 0 || keyIndex >= TRAVEL_KEY_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `键位索引 ${keyIndex} 无效（0-${TRAVEL_KEY_COUNT - 1}）`,
      )
    }
    const block = await readTravelBlock(this.gcomeDevice)
    return decodeShaftTypeByte(block[keyIndex * TRAVEL_KEY_SIZE])
  }

  /** 设置指定键位的轴体类型 ID。 */
  async setKeyShaftType(keyIndex: number, shaftType: number): Promise<void> {
    if (!Number.isInteger(keyIndex) || keyIndex < 0 || keyIndex >= TRAVEL_KEY_COUNT) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `键位索引 ${keyIndex} 无效（0-${TRAVEL_KEY_COUNT - 1}）`,
      )
    }
    // encodeShaftTypeByte 会校验 shaftType 范围（0-31），非法抛 RangeError
    const byte0 = encodeShaftTypeByte(shaftType)
    const block = await readTravelBlock(this.gcomeDevice)
    block[keyIndex * TRAVEL_KEY_SIZE] = byte0
    await writeTravelBlock(this.gcomeDevice, block)
  }
  // ==================== 校准 / 行程演示 ====================

  /** 功能变量区字节7（Perf_Cfg_mask）Bit2：智能动态校准。 */
  private static readonly PERF_MASK_ADAPTIVE_CALIB = 0x04
  /** 功能变量区字节7（Perf_Cfg_mask）Bit3：调试模式/设备上传按键信息（校准与行程演示共用开关）。 */
  private static readonly PERF_MASK_DEBUG_UPLOAD = 0x08
  /** 校准保活间隔：PC 需每隔 2s 发送 0xa8 维持校准状态，设备 5s 未收到自动退出。 */
  private static readonly CALIBRATION_KEEPALIVE_MS = 2000

  private calibrationTimer: ReturnType<typeof setInterval> | null = null

  private stopCalibrationKeepalive(): void {
    if (this.calibrationTimer !== null) {
      clearInterval(this.calibrationTimer)
      this.calibrationTimer = null
    }
  }

  private async sendCalibrationCommand(command: number, data?: number[]): Promise<void> {
    await this.gcomeDevice.sendRawApCommand({
      command,
      data: data ?? [],
    })
  }

  /** 根据按键生成校准选择位图（每个 bit 对应一个按键索引，=1 开启该键校准；不传 = 全局校准）。 */
  private buildCalibrationBitmap(keyList?: baseKeyMap): number[] {
    if (!keyList) return []
    const bitmap = new Array(16).fill(0)
    const idx = keyList.index
    if (Number.isInteger(idx) && idx >= 0 && idx < 128) {
      bitmap[idx >> 3] |= 1 << (idx & 7)
    }
    return bitmap
  }

  /**
   * 切换演示模式（行程动态演示）：即"调试模式/设备上传按键信息"开关
   * （功能变量区字节7 Bit3）。开启后设备持续上报按键调试包（key_debug），
   * 由设备层解析并以 ReportEvents.KeyCalibUpload 事件抛出。
   */
  async switchDemoMode(state: boolean): Promise<void> {
    await mutateFuncBlock(this.gcomeDevice, (block) => {
      block[7] = state
        ? block[7] | GcomeApKeyTravel.PERF_MASK_DEBUG_UPLOAD
        : block[7] & ~GcomeApKeyTravel.PERF_MASK_DEBUG_UPLOAD
    })
  }

  /**
   * 切换键盘校准模式。
   * - true：发送 0xa8 开始全局校准，并启动每 2s 一次的保活定时器（设备 5s 未收到自动退出）；
   * - false：停止保活并发送 0xa9 结束校准。
   * @param options 预留：按键级校准选择（protocolCailbrationType 与 CH585M 语义不同，忽略）
   */
  async switchCalibrationMode(state: boolean, _options?: keyboardCalibration): Promise<void> {
    if (state) {
      await this.sendCalibrationCommand(GCOME_AP_CMD.startCalibration)
      this.stopCalibrationKeepalive()
      this.calibrationTimer = setInterval(() => {
        void this.sendCalibrationCommand(GCOME_AP_CMD.startCalibration).catch(() => {
          this.stopCalibrationKeepalive()
        })
      }, GcomeApKeyTravel.CALIBRATION_KEEPALIVE_MS)
    } else {
      this.stopCalibrationKeepalive()
      await this.sendCalibrationCommand(GCOME_AP_CMD.endCalibration)
    }
  }

  /**
   * 协议校准：发送 0xa8 开始校准。
   * GCOME_AP 无 ATI/SMD 之分（全局校准），type 参数被忽略；
   * 提供 keyList 时按 bit 位图选择按键（协议预留功能），空列表为全局校准。
   */
  async calibration(_type: protocolCailbrationType, keyList?: baseKeyMap): Promise<void> {
    await this.sendCalibrationCommand(
      GCOME_AP_CMD.startCalibration,
      this.buildCalibrationBitmap(keyList),
    )
  }

  /**
   * 保存协议校准数据：发送 0xa9 结束校准并停止保活。
   * 0xa9 响应同时携带快速校准值（可用 getCalibrationValuesQuick 读取）。
   */
  async saveCalibration(_type: protocolCailbrationType): Promise<void> {
    this.stopCalibrationKeepalive()
    await this.sendCalibrationCommand(GCOME_AP_CMD.endCalibration)
  }

  /**
   * 切换智能动态校准（功能变量区字节7 Bit2）。
   * 校准与行程演示仍需要 Bit3 调试模式开关配合（switchDemoMode）。
   */
  async switchAdaptiveCalibration(state: boolean): Promise<void> {
    await mutateFuncBlock(this.gcomeDevice, (block) => {
      block[7] = state
        ? block[7] | GcomeApKeyTravel.PERF_MASK_ADAPTIVE_CALIB
        : block[7] & ~GcomeApKeyTravel.PERF_MASK_ADAPTIVE_CALIB
    })
  }

  /** 读取当前校准信息区起始地址（依赖当前板载分组）。 */
  private async calibrationZoneBase(): Promise<number> {
    const res = await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.getBase,
      data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
    })
    const profile = res.response.data[0] ?? 0
    return calculateGcomeApAddress(GCOME_AP_CMD.getCalibrationInfo, profile)
  }

  /**
   * 读取按键校准数据（0xaa，512 字节区 = 128 键 × 4 字节 IEEE754 单精度浮点，1.81-6.00）。
   * GCOME_AP 特有接口，供测试台/上位机展示校准值。
   */
  async getCalibrationValues(): Promise<GcomeCalibrationValue[]> {
    const device = this.gcomeDevice
    const base = await this.calibrationZoneBase()
    const chunks: Uint8Array[] = []
    for (
      let offset = 0;
      offset < GCOME_CALIBRATION_ZONE_SIZE;
      offset += GCOME_AP_REPORT.payloadSize
    ) {
      const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, GCOME_CALIBRATION_ZONE_SIZE - offset)
      const res = await device.sendRawApCommand({
        command: GCOME_AP_CMD.getCalibrationInfo,
        address: base + offset,
        data: new Array(frameLen).fill(0),
      })
      chunks.push(res.response.data)
      if (res.response.data.length < frameLen) break
    }
    const merged = new Uint8Array(GCOME_CALIBRATION_ZONE_SIZE)
    let offset = 0
    for (const chunk of chunks) {
      if (offset >= GCOME_CALIBRATION_ZONE_SIZE) break
      merged.set(chunk.slice(0, GCOME_CALIBRATION_ZONE_SIZE - offset), offset)
      offset += chunk.length
    }
    return parseCalibrationValues(merged)
  }

  /**
   * 快速获取按键校准值（0xa9 结束命令响应携带，分帧读满 512 字节）。
   * 文档说明：0xa9 可快速获取按键校准值，保留小数点后 2 位。
   */
  async getCalibrationValuesQuick(): Promise<GcomeCalibrationValue[]> {
    const device = this.gcomeDevice
    const chunks: Uint8Array[] = []
    for (
      let offset = 0;
      offset < GCOME_CALIBRATION_ZONE_SIZE;
      offset += GCOME_AP_REPORT.payloadSize
    ) {
      const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, GCOME_CALIBRATION_ZONE_SIZE - offset)
      const res = await device.sendRawApCommand({
        command: GCOME_AP_CMD.endCalibration,
        data: new Array(frameLen).fill(0),
      })
      chunks.push(res.response.data)
      if (res.response.data.length < frameLen) break
    }
    const merged = new Uint8Array(GCOME_CALIBRATION_ZONE_SIZE)
    let offset = 0
    for (const chunk of chunks) {
      if (offset >= GCOME_CALIBRATION_ZONE_SIZE) break
      merged.set(chunk.slice(0, GCOME_CALIBRATION_ZONE_SIZE - offset), offset)
      offset += chunk.length
    }
    return parseCalibrationValues(merged)
  }

  /**
   * 读取按键校准次数（共用 0x04 GetBase，响应偏移 128 起，顺序同矩阵信息；0xff = 校准完成）。
   * 若固件响应不足 128 字节（GetBase 仅返回基础信息），返回空数组。
   */
  async getCalibrationCounts(): Promise<number[]> {
    const res = await this.gcomeDevice.sendRawApCommand({
      command: GCOME_AP_CMD.getBase,
      data: new Array(GCOME_AP_REPORT.payloadSize).fill(0),
    })
    const data = Array.from(res.response.data)
    if (data.length <= 128) return []
    return data.slice(128, 128 + GCOME_CALIBRATION_KEY_COUNT)
  }
}
